import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { db } from '../../firebase/firebaseConfig';
import { ref, onValue } from 'firebase/database';
import { DAIV_VERSION } from '../../services/aiService';
import '../../components/AIAesthetics.css';

function AdminAIHub() {
  const [chatMetrics, setChatMetrics] = useState({
    totalSessions: 0,
    totalMessages: 0,
    uniqueUsers: 0,
    activityChart: []
  });
  const [stability, setStability] = useState({ index: 100, status: 'Optimal' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 1. Fetch Real Chat Data for AI Hub
    const chatsRef = ref(db, 'chats');
    const unsubscribeChats = onValue(chatsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        let sessionCount = 0;
        let messageCount = 0;
        let users = Object.keys(data).length;
        
        // Count sessions per user for activity bar chart
        const activityData = [];

        for (const uid in data) {
          const userSessions = Object.keys(data[uid]).length;
          sessionCount += userSessions;
          
          activityData.push({
            name: `User ${uid.substring(0,4)}`,
            sessions: userSessions
          });

          for (const sessionId in data[uid]) {
            const msgs = data[uid][sessionId].messages || {};
            messageCount += Object.keys(msgs).length;
          }
        }

        // Sort by highest engagement
        activityData.sort((a,b) => b.sessions - a.sessions);
        const top5 = activityData.slice(0, 5);

        setChatMetrics({
          totalSessions: sessionCount,
          totalMessages: messageCount,
          uniqueUsers: users,
          activityChart: top5.length > 0 ? top5 : [{name: 'No Data', sessions: 0}]
        });
      } else {
         setChatMetrics({
          totalSessions: 0,
          totalMessages: 0,
          uniqueUsers: 0,
          activityChart: [{name: 'No Data', sessions: 0}]
        });
      }
      setLoading(false);
    });

    // 2. Platform Stability based on Open Reports vs Total Users
    const usersRef = ref(db, 'users');
    const reportsRef = ref(db, 'reports');
    
    let totalUsers = 1;
    const unsubUsers = onValue(usersRef, snap => {
      if(snap.exists()) totalUsers = Object.keys(snap.val()).length;
    });

    const unsubReports = onValue(reportsRef, snap => {
      let activeReports = 0;
      if (snap.exists()) {
        const reps = Object.values(snap.val());
        activeReports = reps.filter(r => r.status === 'open').length;
      }
      // Index drops if there are many open reports relative to users
      const drop = (activeReports / totalUsers) * 100;
      const index = Math.max(0, 100 - drop).toFixed(1);
      
      let status = "Optimal";
      if (index < 95) status = "Elevated Risk";
      if (index < 80) status = "Critical Volatility";
      
      setStability({ index, status });
    });

    return () => {
      unsubscribeChats();
      unsubUsers();
      unsubReports();
    };
  }, []);

  if (loading) return (
    <div className="ai-analyst-container" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px'}}>
       <div className="persona-icon-wrapper"><i className="fa-solid fa-microchip fa-spin"></i></div>
       <p style={{color: 'var(--brand-accent)', marginLeft: '20px'}}>Initializing Origin Core Oversight...</p>
    </div>
  );

  return (
    <div className="animate-up">
      <div className="admin-page-header">
        <h1 style={{fontSize: '2.5rem', fontWeight: '800', letterSpacing: '-1.5px'}}>
          E2EE Chat Intel <span className="daiv-version-tag">{DAIV_VERSION}</span>
        </h1>
        <p style={{color: 'var(--text-secondary)', marginTop: '5px'}}>
          Real-time oversight of end-to-end encrypted consultations and global platform health.
        </p>
      </div>

      {/* High Level Stats Strip */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px'}}>
        <div className="ai-sub-card" style={{margin: 0, textAlign: 'center'}}>
          <i className="fa-regular fa-message" style={{color: 'var(--brand-accent)', fontSize: '1.5rem', marginBottom: '10px'}}></i>
          <h3 style={{fontSize: '2rem', margin: '0 0 5px 0', color: '#fff'}}>{chatMetrics.totalSessions}</h3>
          <p style={{color: 'var(--text-secondary)', fontSize: '0.85rem'}}>Total Chat Sessions</p>
        </div>
        <div className="ai-sub-card" style={{margin: 0, textAlign: 'center'}}>
          <i className="fa-solid fa-users-viewfinder" style={{color: 'var(--brand-accent)', fontSize: '1.5rem', marginBottom: '10px'}}></i>
          <h3 style={{fontSize: '2rem', margin: '0 0 5px 0', color: '#fff'}}>{chatMetrics.uniqueUsers}</h3>
          <p style={{color: 'var(--text-secondary)', fontSize: '0.85rem'}}>Unique Chat Users</p>
        </div>
        <div className="ai-sub-card" style={{margin: 0, textAlign: 'center'}}>
          <i className="fa-solid fa-shield-halved" style={{color: 'var(--brand-accent)', fontSize: '1.5rem', marginBottom: '10px'}}></i>
          <h3 style={{fontSize: '2rem', margin: '0 0 5px 0', color: '#fff'}}>{chatMetrics.totalMessages}</h3>
          <p style={{color: 'var(--text-secondary)', fontSize: '0.85rem'}}>Encrypted Messages</p>
        </div>
      </div>

      <div className="ai-grid">
        {/* User Engagement Forecast / Stats */}
        <div className="ai-sub-card" style={{gridColumn: '1 / -1'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px'}}>
             <h3 style={{color: '#fff', margin: 0, fontSize: '1.4rem'}}><i className="fa-solid fa-chart-line"></i> Top Consultation Nodes</h3>
          </div>
          
          <div style={{width: '100%', height: '300px'}}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chatMetrics.activityChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="name" stroke="#8b949e" />
                <YAxis stroke="#8b949e" />
                <Tooltip 
                  contentStyle={{background: '#0d1117', border: '1px solid var(--brand-accent)', borderRadius: '12px'}}
                />
                <Bar dataKey="sessions" fill="var(--brand-accent)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Stability Index */}
        <div className="ai-sub-card">
          <div className="ai-card-title">
            <i className="fa-solid fa-shield-heart"></i> Platform Health
          </div>
          <div style={{textAlign: 'center', padding: '20px 0'}}>
             <div style={{fontSize: '3.5rem', fontWeight: '800', color: '#fff'}}>{stability.index}%</div>
             <p style={{color: 'var(--brand-accent)', fontWeight: '600'}}>{stability.status}</p>
          </div>
        </div>

        {/* Anomaly Detection (v1.0) */}
        <div className="ai-sub-card">
          <div className="ai-card-title">
            <i className="fa-solid fa-tower-broadcast"></i> Status Check
          </div>
          <div className="roadmap-timeline">
             <div className="roadmap-step active" style={{padding: '12px'}}>
                <div className="step-icon" style={{width: '35px', height: '35px'}}>
                   <i className={chatMetrics.totalMessages > 0 ? "fa-solid fa-bolt" : "fa-solid fa-check"}></i>
                </div>
                <div className="step-info">
                   <h4 style={{fontSize: '0.9rem'}}>{chatMetrics.totalMessages > 0 ? "E2EE Stream Active" : "Systems Normal"}</h4>
                   <p style={{fontSize: '0.75rem'}}>{chatMetrics.totalMessages > 0 ? "Client-side encryption pipelines online." : "Standard platform monitoring active."}</p>
                </div>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminAIHub;

